import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { getPricingTreatments, hasCustomPricingData } from '@/lib/treatmentStore';

const hairSlugs: Record<string, string> = {
  'FUE Haartransplantation (bis 5.000 Grafts)': 'hair-transplant',
};

const TECHNIQUE_DETAILS = [
  { icon: 'ri-magic-line', labelKey: 'svc.hair_tech_method_label', valueKey: 'svc.hair_tech_method_value' },
  { icon: 'ri-focus-3-line', labelKey: 'svc.hair_tech_grafts_label', valueKey: 'svc.hair_tech_grafts_value' },
  { icon: 'ri-time-line', labelKey: 'svc.hair_tech_duration_label', valueKey: 'svc.hair_tech_duration_value' },
  { icon: 'ri-psychotherapy-line', labelKey: 'svc.hair_tech_anesthesia_label', valueKey: 'svc.hair_tech_anesthesia_value' },
  { icon: 'ri-shield-check-line', labelKey: 'svc.hair_tech_guarantee_label', valueKey: 'svc.hair_tech_guarantee_value' },
  { icon: 'ri-flight-takeoff-line', labelKey: 'svc.hair_tech_return_label', valueKey: 'svc.hair_tech_return_value' },
];

const WHATS_INCLUDED_KEYS = [
  'svc.hair_incl_1',
  'svc.hair_incl_2',
  'svc.hair_incl_3',
  'svc.hair_incl_4',
  'svc.hair_incl_5',
  'svc.hair_incl_6',
];

const GRAFT_PACKAGES = [
  { grafts: 'svc.hair_graft1_count', descKey: 'svc.hair_graft1_desc', idealKey: 'svc.hair_graft1_ideal', graftsIsKey: true },
  { grafts: '2.000–3.500', descKey: 'svc.hair_graft2_desc', idealKey: 'svc.hair_graft2_ideal', graftsIsKey: false },
  { grafts: '3.500–5.000', descKey: 'svc.hair_graft3_desc', idealKey: 'svc.hair_graft3_ideal', graftsIsKey: false },
];

interface TreatmentItem {
  name: string;
  price: string;
  stay?: string;
}

function getTreatments(lang: string): TreatmentItem[] {
  return getPricingTreatments('hair', lang);
}

function getSlugByName(name: string): string | undefined {
  return hairSlugs[name];
}

export default function HairTransplantPriceTable() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const items = getTreatments(lang);

  return (
    <section id="hair" className="py-16 md:py-24 px-4 md:px-10 bg-background-50">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12 md:mb-16">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-[10px] md:text-xs tracking-[0.15em] uppercase font-label">
            <i className="ri-user-voice-line text-xs"></i>
            {t('pricing.hair_title')}
          </span>
          <h2 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-foreground-900">
            {t('pricing.hair_title')}
          </h2>
          <p className="mt-3 text-sm text-foreground-500 font-body max-w-lg mx-auto leading-relaxed break-words">
            {t('svc.hair_subtitle')}
          </p>
        </div>

        {/* All treatments rendered as cards */}
        <div className="space-y-6">
          {items.map((item, idx) => {
            const slug = getSlugByName(item.name);
            return (
              <div key={idx} className="rounded-2xl border-2 border-accent-200 bg-gradient-to-br from-accent-50/60 to-background-50 overflow-hidden">
                <div className="p-6 md:p-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-1 flex flex-col justify-center">
                    <span className="text-[10px] tracking-[0.2em] uppercase text-accent-600 font-label">
                      {idx === 0 ? t('svc.hair_bestseller') : t('svc.hair_treatment_label')}
                    </span>
                    <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-1">{item.name}</h3>
                    {item.stay && (
                      <p className="text-xs text-foreground-500 font-body mt-1">{item.stay}</p>
                    )}
                    <div className="mt-4">
                      <span className="text-[11px] tracking-[0.15em] uppercase text-foreground-400 font-label">{t('svc.hair_total_price')}</span>
                      <p className="font-heading text-3xl md:text-4xl font-bold text-accent-600 mt-0.5">
                        {item.price} €
                      </p>
                      <span className="text-[10px] text-foreground-400 font-body">{t('svc.hair_all_inclusive')}</span>
                    </div>
                    {slug && (
                      <Link
                        to={`/services/${slug}`}
                        className="mt-5 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-accent-500 text-white text-sm font-label hover:bg-accent-600 transition-colors cursor-pointer no-underline whitespace-nowrap"
                      >
                        {t('pricing.cta')}
                        <i className="ri-arrow-right-line"></i>
                      </Link>
                    )}
                  </div>

                  <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {TECHNIQUE_DETAILS.map((detail, didx) => (
                      <div key={didx} className="flex items-start gap-3 p-3 rounded-xl bg-background-50 border border-background-200/60">
                        <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-lg bg-accent-100">
                          <i className={`${detail.icon} text-sm text-accent-600`}></i>
                        </div>
                        <div>
                          <p className="text-[10px] tracking-[0.15em] uppercase text-foreground-400 font-label">{t(detail.labelKey)}</p>
                          <p className="text-xs font-body text-foreground-800 mt-0.5 leading-snug break-words">{t(detail.valueKey)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Only show graft packages & what's included for first item (classic FUE) */}
                {idx === 0 && (
                  <>
                    <div className="border-t border-accent-200/50"></div>
                    <div className="p-6 md:p-8">
                      <h4 className="font-label text-sm font-semibold text-foreground-900 mb-4">{t('svc.hair_graft_overview')}</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {GRAFT_PACKAGES.map((pkg, pidx) => (
                          <div
                            key={pidx}
                            className={`p-4 rounded-xl border transition-all duration-300 ${
                              pidx === 1
                                ? 'bg-accent-50 border-accent-300'
                                : 'bg-background-50 border-background-200/70 hover:border-accent-200'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-heading text-lg font-bold text-foreground-900">{pkg.graftsIsKey ? t(pkg.grafts) : pkg.grafts}</span>
                              {pidx === 1 && (
                                <span className="px-2 py-0.5 rounded-full bg-accent-500 text-[9px] text-white font-label">{t('svc.hair_popular')}</span>
                              )}
                            </div>
                            <p className="text-xs text-foreground-600 font-body leading-snug break-words">{t(pkg.descKey)}</p>
                            <div className="mt-2 pt-2 border-t border-background-200/60">
                              <span className="text-[10px] text-foreground-400 font-label">{t('svc.hair_ideal_for')}</span>
                              <p className="text-xs text-foreground-800 font-body mt-0.5">{t(pkg.idealKey)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="border-t border-accent-200/50 p-6 md:p-8 bg-accent-50/30">
                      <h4 className="font-label text-sm font-semibold text-foreground-900 mb-3">{t('svc.hair_included_title')}</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                        {WHATS_INCLUDED_KEYS.map((incKey, iidx) => (
                          <div key={iidx} className="flex items-center gap-2 text-xs font-body text-foreground-700">
                            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                              <i className="ri-check-line text-accent-500 text-xs"></i>
                            </div>
                            <span dangerouslySetInnerHTML={{ __html: t(incKey) }}></span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>

        {items.length === 0 && (
          <div className="text-center py-12">
            <p className="text-sm text-foreground-400 font-body">{t('svc.hair_none_available')}</p>
          </div>
        )}

        <p className="mt-8 text-center text-xs text-foreground-400 font-body">
          {t('pricing.note_individual')}
        </p>
      </div>
    </section>
  );
}